import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { InteriorSlide, SlideBanner } from './SlideShell';
import type { SlideProps } from './SlideShell';
import { PdfImage } from '../PdfImage';
import { boehringerImages } from '../../data/boehringerSnak';
const STAGES = [{
  phase: 'Attract',
  label: 'Hook',
  line: 'Earn the tap.',
  crop: boehringerImages.emailFull,
  alt: 'Boehringer email inviting GPs to find their high-risk patients'
}, {
  phase: 'Engage',
  label: 'Snak',
  line: 'Earn the interaction.',
  crop: boehringerImages.quizScreen,
  alt: 'Snak quiz asking what share of T2D and CVD patients receive neither SGLT2 nor GLP-1'
}, {
  phase: 'Learn',
  label: 'Signals',
  line: 'Understand what HCPs think.',
  crop: boehringerImages.quizResults,
  alt: 'Answer distribution showing 53% correct and 47% incorrect'
}, {
  phase: 'Improve',
  label: 'Next campaign',
  line: 'Use the learning.',
  crop: boehringerImages.ctaScreen,
  alt: 'Snak menu offering KiAP material and the next online meeting'
}] as const;
export function SlideSnakToCampaign({}: SlideProps) {
  return <InteriorSlide surface banner={<SlideBanner data-id="element-349" data-mp="10qnvxuksla62">Every campaign makes the next one smarter.</SlideBanner>} data-id="element-348" data-mp="10pcsjvv9e4fh">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="flex-shrink-0" data-id="element-350" data-mp="14qy0r3wulujc">
        <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-351" data-mp="d138aydp1oqg">
          From Snak to campaign
        </p>
        <h2 className="max-w-4xl text-[clamp(1.9rem,3.6vw,3.3rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-352" data-mp="lsdbziifj18v">
          One Snak can become a{' '}
          <span className="text-[var(--brand)]" data-id="element-353" data-mp="3le0oxjcma1gf">continuous campaign.</span>
        </h2>
      </motion.header>

      <div className="mt-[clamp(1rem,2vw,1.75rem)] flex min-h-0 flex-1 items-stretch gap-[clamp(0.4rem,1vw,1rem)]" data-id="element-354" data-mp="3pfcbth43w8kx">
        {STAGES.map((stage, index) => <React.Fragment key={stage.phase} data-id="element-355" data-mp="rnc350w58spy" data-mpi={index}>
            <motion.figure initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2 + index * 0.12,
          duration: 0.5
        }} className="flex min-h-0 min-w-0 flex-1 flex-col items-center" data-id="element-356" data-mp="16d1ka5kmt7bv">
              <span className="flex-shrink-0 rounded-full bg-[var(--brand)] px-[clamp(0.6rem,1vw,0.95rem)] py-[clamp(0.2rem,0.4vw,0.35rem)] text-[clamp(0.55rem,0.8vw,0.72rem)] font-bold uppercase tracking-[0.18em] text-white" data-id="element-357" data-mp="dqgmondkbyzv">
                {stage.phase}
              </span>
              <div className="mt-[clamp(0.5rem,1vw,0.9rem)] flex min-h-0 flex-1 items-center justify-center" data-id="element-358" data-mp="ilvxt7k6rgsl">
                <PdfImage crop={stage.crop} alt={stage.alt} className="max-h-full w-auto max-w-full rounded-[1.25rem] bg-white object-contain drop-shadow-[0_18px_40px_rgba(23,32,51,0.16)]" data-id="element-359" data-mp="1y2e9uvmmc0nv" />
              </div>
              <figcaption className="mt-[clamp(0.5rem,1vw,0.9rem)] flex-shrink-0 text-center" data-id="element-360" data-mp="26msx50xrx9ev">
                <p className="text-[clamp(0.95rem,1.4vw,1.35rem)] font-extrabold leading-tight tracking-[-0.025em] text-[var(--fg)]" data-id="element-361" data-mp="1rp8u3zkftobc">
                  {stage.label}
                </p>
                <p className="mt-0.5 text-[clamp(0.7rem,1vw,0.92rem)] text-[var(--muted)]" data-id="element-362" data-mp="2kdnyzt9uuq8y">
                  {stage.line}
                </p>
              </figcaption>
            </motion.figure>

            {index < STAGES.length - 1 && <motion.span initial={{
          opacity: 0
        }} animate={{
          opacity: 1,
          x: [0, 5, 0]
        }} transition={{
          opacity: {
            delay: 0.3 + index * 0.12,
            duration: 0.4
          },
          x: {
            duration: 1.8,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: index * 0.3
          }
        }} className="flex h-9 w-9 flex-shrink-0 items-center justify-center self-center rounded-full bg-[var(--brand)] text-white shadow-sm" aria-hidden="true" data-id="element-363" data-mp="2akqjlw05thek">
                <ArrowRightIcon className="h-4 w-4" data-id="element-364" data-mp="15h4wbvq0nsvh" />
              </motion.span>}
          </React.Fragment>)}
      </div>
    </InteriorSlide>;
}